import React, { useState, useEffect, useCallback } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { CheckCircle, CreditCard, Lock, QrCode, DeviceMobile } from 'phosphor-react';
import { QRCodeSVG } from 'qrcode.react';

const StripeCheckout = ({ orderData, onPaymentSuccess, onPaymentError, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'qr'
  const [qrPaymentUrl, setQrPaymentUrl] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [qrPollInterval, setQrPollInterval] = useState(null);

  const startPaymentPolling = useCallback((payment_link_id) => {
    // Poll the backend to check payment status
    const pollInterval = setInterval(async () => {
      try {
        // Check payment status with real Stripe API
        const apiUrl = process.env.NODE_ENV === 'development' 
          ? 'http://localhost:3001' 
          : '';
        
        const response = await fetch(`${apiUrl}/api/check-payment-status?payment_link_id=${payment_link_id}`);
        
        if (response.ok) {
          const { status, payment_intent } = await response.json();
          
          if (status === 'completed') {
            clearInterval(pollInterval);
            setQrPollInterval(null);
            
            // Payment successful
            onPaymentSuccess({
              paymentMethod: { type: 'qr_code' },
              paymentId: payment_intent.id,
              amount: payment_intent.amount,
              payment_link_id: payment_link_id
            });
          }
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        // Continue polling on error
      }
    }, 3000); // Check every 3 seconds
    
    setQrPollInterval(pollInterval);
    
    // Auto-timeout after 10 minutes
    setTimeout(() => {
      if (pollInterval) {
        clearInterval(pollInterval);
        setQrPollInterval(null);
        setError('QR payment timed out. Please try again or use card payment.');
      }
    }, 600000); // 10 minutes
  }, [onPaymentSuccess]);

  const generateQRPayment = useCallback(async () => {
    setQrLoading(true);
    setError('');
    
    try {
      // Call the backend API to create a real Stripe Payment Link
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001' 
        : '';
      
      const response = await fetch(`${apiUrl}/api/create-payment-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: orderData.total_amount * 100, // Stripe uses cents
          currency: orderData.currency.toLowerCase(),
          order_id: orderData.order_number,
          items: orderData.items.map(item => ({
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            product_id: item.product_id
          })),
          metadata: {
            user_id: orderData.user_id,
            user_email: orderData.user_email,
            payment_method: 'qr_code'
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create payment link');
      }

      const { payment_url, payment_link_id } = await response.json();
      
      // Store the payment link ID for later reference
      console.log('Payment URL:', payment_url);
      setQrPaymentUrl(payment_url);
      
      // Start polling for payment completion
      startPaymentPolling(payment_link_id);
      
    } catch (err) {
      console.error('Error generating QR payment:', err);
      let errorMessage = `Failed to generate QR payment: ${err.message}`;
      
      // Handle specific Stripe errors
      if (err.message.includes('amount_too_small')) {
        errorMessage = 'Order total must be at least €0.50 for payment processing.';
      }
      
      setError(`${errorMessage}. Please try card payment instead.`);
    } finally {
      setQrLoading(false);
    }
  }, [orderData, startPaymentPolling]);

  // Generate QR payment URL when QR tab is selected
  useEffect(() => {
    if (paymentMethod === 'qr' && !qrPaymentUrl) {
      generateQRPayment();
    }
  }, [paymentMethod, qrPaymentUrl, generateQRPayment]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (qrPollInterval) {
        clearInterval(qrPollInterval);
      }
    };
  }, [qrPollInterval]);

  const handleCardSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    const cardElement = elements.getElement(CardElement);

    try {
      // Create payment method
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: `${orderData.shipping_address?.first_name || ''} ${orderData.shipping_address?.last_name || ''}`.trim(),
          email: orderData.user_email,
          address: {
            street: orderData.shipping_address?.street_and_housenumber,
            line1: orderData.shipping_address?.address_line_1,
            line2: orderData.shipping_address?.address_line_2,
            city: orderData.shipping_address?.city,
            postal_code: orderData.shipping_address?.postal_code,
            country: orderData.shipping_address?.country === 'Belgium' ? 'BE' : 'BE',
          },
        },
      });

      if (paymentMethodError) {
        setError(paymentMethodError.message);
        setLoading(false);
        return;
      }

      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Simulate successful payment (90% success rate for demo)
      const paymentSuccessful = Math.random() > 0.1;

      if (paymentSuccessful) {
        onPaymentSuccess({
          paymentMethod: paymentMethod,
          paymentId: `pi_card_${Date.now()}`,
          amount: orderData.total_amount * 100,
        });
      } else {
        setError('Payment failed. Please try again.');
        onPaymentError(new Error('Payment declined'));
      }

    } catch (err) {
      setError('An error occurred during payment processing.');
      onPaymentError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = useCallback(() => {
    if (qrPollInterval) {
      clearInterval(qrPollInterval);
      setQrPollInterval(null);
    }
    onCancel();
  }, [qrPollInterval, onCancel]);

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
        padding: '12px',
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200">
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            {paymentMethod === 'card' ? (
              <CreditCard size={20} className="text-green-600" />
            ) : (
              <QrCode size={20} className="text-green-600" />
            )}
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Payment Details</h2>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600">Total Amount:</span>
            <span className="text-xl font-bold text-ambre-600">€{orderData.total_amount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center text-sm text-gray-500">
            <span>Order: {orderData.order_number}</span>
            <span>{orderData.items.length} item{orderData.items.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Payment Method Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setPaymentMethod('card')}
            className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition ${
              paymentMethod === 'card'
                ? 'border-ambre-500 text-ambre-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <CreditCard size={18} />
              <span>Card Payment</span>
            </div>
          </button>
          <button
            onClick={() => setPaymentMethod('qr')}
            className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition ${
              paymentMethod === 'qr'
                ? 'border-ambre-500 text-ambre-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <QrCode size={18} />
              <span>QR Code</span>
            </div>
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Card Payment Form */}
      {paymentMethod === 'card' && (
        <form onSubmit={handleCardSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Information
            </label>
            <div className="border border-gray-300 rounded-lg p-4 focus-within:ring-2 focus-within:ring-ambre-500 focus-within:border-ambre-500">
              <CardElement options={cardElementOptions} />
            </div>
          </div>

          <div className="flex items-center justify-center text-xs text-gray-500 mb-6">
            <Lock size={16} className="mr-2" />
            <span>Your payment information is secure and encrypted</span>
          </div>

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
              disabled={loading}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={!stripe || loading}
              className="flex-1 bg-ambre-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ambre-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle size={20} className="mr-2" />
                  Pay €{orderData.total_amount.toFixed(2)}
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* QR Code Payment */}
      {paymentMethod === 'qr' && (
        <div className="text-center">
          {qrLoading ? (
            <div className="py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ambre-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Generating QR code...</p>
            </div>
          ) : qrPaymentUrl ? (
            <>
              <div className="bg-white p-6 rounded-lg border-2 border-gray-200 mb-6 inline-block">
                <QRCodeSVG
                  value={qrPaymentUrl}
                  size={200}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                />
              </div>
              
              <div className="mb-6">
                <div className="flex items-center justify-center space-x-2 text-ambre-600 mb-2">
                  <DeviceMobile size={20} />
                  <span className="font-semibold">Scan to Pay with Stripe</span>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Scan this QR code with your phone to open a secure Stripe payment page
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Payment amount: €{orderData.total_amount.toFixed(2)}
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Secure Payment:</strong> This QR code links to a real Stripe checkout page. You can pay with any card, Apple Pay, Google Pay, or other payment methods supported by Stripe.
                  </p>
                </div>
              </div>

              {qrPollInterval && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center space-x-2 text-blue-600 mb-2">
                    <div className="animate-pulse w-2 h-2 bg-blue-600 rounded-full"></div>
                    <span className="font-medium">Waiting for payment...</span>
                  </div>
                  <p className="text-sm text-blue-600">
                    Please complete the payment on your mobile device
                  </p>
                </div>
              )}

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
                
                <button
                  onClick={generateQRPayment}
                  className="flex-1 bg-ambre-100 text-ambre-700 px-6 py-3 rounded-lg font-semibold hover:bg-ambre-200 transition flex items-center justify-center"
                >
                  <QrCode size={20} className="mr-2" />
                  Refresh QR Code
                </button>
              </div>
            </>
          ) : (
            <div className="py-12">
              <QrCode size={64} className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Unable to generate QR code</p>
              <button
                onClick={generateQRPayment}
                className="bg-ambre-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ambre-700 transition"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-6 text-xs text-gray-500 text-center">
        <p>By completing your purchase, you agree to our terms of service.</p>
      </div>
    </div>
  );
};

export default StripeCheckout; 